import { useMemo, useState } from "react";
import { PageHeader, SearchInput } from "@/pages/admin/components/AdminUI";
import HelpFaqList from "@/pages/admin/components/HelpFaqList";
import { adminHelpTopics, platformSupport } from "@/pages/admin/helpContent";

export default function AdminHelp() {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const topics = useMemo(() => {
    if (!q) return adminHelpTopics;
    return adminHelpTopics
      .map((topic) => ({
        ...topic,
        faqs: topic.faqs.filter((faq) =>
          `${faq.q} ${faq.a} ${faq.toLabel || ""}`.toLowerCase().includes(q),
        ),
      }))
      .filter((topic) => topic.faqs.length > 0);
  }, [q]);

  const total = adminHelpTopics.reduce((sum, topic) => sum + topic.faqs.length, 0);

  return (
    <div>
      <PageHeader
        eyebrow="Support"
        title="Help & Support"
        subtitle="Quick answers to the questions the team hits most often — each one jumps straight to the module that does the job."
      />

      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
        <SearchInput value={query} onChange={setQuery} placeholder="Search help — e.g. approve, refund, export, feature…" />
        <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
          {total} quick answers across {adminHelpTopics.length} topics
        </span>
      </div>

      {topics.length === 0 ? (
        <div className="bg-background-50 border border-background-200 rounded-lg py-16 px-6 text-center">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
            No answers match “{query}”
          </h3>
          <p className="text-foreground-600 mt-3 max-w-md mx-auto">
            Try a different word, or reach the platform documentation below.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
          {topics.map((topic) => (
            <section
              key={topic.id}
              className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-900 shrink-0">
                  <i className={`${topic.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <h2 className="font-heading text-lg text-foreground-950 font-semibold">
                    {topic.title}
                  </h2>
                  <p className="text-xs text-foreground-500">{topic.blurb}</p>
                </div>
              </div>
              <HelpFaqList faqs={topic.faqs} />
            </section>
          ))}
        </div>
      )}

      <section className="mt-6 bg-primary-950 text-background-100 rounded-lg p-6 md:p-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
          <div className="max-w-2xl">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-accent-950">
              <i className="ri-lifebuoy-line text-xl"></i>
            </span>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mt-4">
              Need help with the platform itself?
            </h2>
            <p className="text-sm text-background-200 leading-relaxed mt-2">
              {platformSupport.blurb}
            </p>
          </div>
          <a
            href={platformSupport.docs}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-accent-500 text-accent-950 text-sm font-semibold px-6 py-3.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer shrink-0"
          >
            <i className="ri-external-link-line"></i>
            {platformSupport.docsLabel}
          </a>
        </div>
      </section>
    </div>
  );
}